/**
 * =============================================================================
 * Hook: useReferralAffiliates (refatorado v3.0.0 — modelo N:N)
 * =============================================================================
 * IMPORTANTE: Após a Fase 4 da migração N:N, a tabela `affiliates` deixou de
 * ter `status`, `available_balance`, `total_*`, `program_id` e
 * `approved_at/by/rejection_reason/terms_accepted_at`. Toda essa "verdade"
 * vive em `affiliate_program_memberships`.
 *
 * Este arquivo deixa de exportar a antiga `useReferralAffiliates({...})` (que
 * fazia SELECT direto na tabela com filtros legados) e passa a expor:
 *   - `useAffiliatesForSelect()` — listagem leve para selects/seletores
 *      (apenas id, full_name, email, avatar_url).
 *   - `useReferralAffiliate(id)` / `useReferralAffiliateLinks(id)` — detalhes
 *      via RPC `get_referral_affiliate_details` (que agora também devolve
 *      `memberships` com totais consolidados).
 *   - `useReferralAffiliateMutations` — apenas `updateNotesMutation` (a
 *      moderação de status passou a ser por adesão via
 *      `useAffiliateMembershipActions`).
 *   - `useCreateAffiliateAdmin` — cria pessoa + adesão via RPC
 *      `create_affiliate` (sem mais escrever colunas legadas em `affiliates`).
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';
import type { AffiliateMembership } from '@/hooks/useReferralAffiliatesPaginated';

const log = createLogger('useReferralAffiliates');

// -----------------------------------------------------------------------------
// Tipos públicos
// -----------------------------------------------------------------------------
export type AffiliateStatus = Database['public']['Enums']['affiliate_status'];

/**
 * Linha de afiliado enriquecida pelos totais consolidados das adesões.
 * Esses totais são injetados pela RPC `get_referral_affiliate_details` (v2.0.0).
 *
 * Como `affiliates` no banco não tem mais status/totais (eles vivem em
 * `affiliate_program_memberships`), trabalhamos com tipo enriquecido aqui.
 */
export type AffiliateRow = Omit<
  Database['public']['Tables']['affiliates']['Row'],
  never
> & {
  /** Status "global" derivado: 'active' se houver pelo menos uma adesão ativa. */
  status?: AffiliateStatus;
  /** Totais agregados de todas as adesões (somatório). */
  available_balance?: number;
  total_earned?: number;
  total_paid?: number;
  total_clicks?: number;
  total_referrals?: number;
  total_conversions?: number;
  total_points?: number;
  /** Não existe mais como coluna; mantido como opcional p/ compat com UI antiga. */
  approved_at?: string | null;
  rejection_reason?: string | null;
};

// -----------------------------------------------------------------------------
// useAffiliatesForSelect — listagem leve para seletores administrativos
// -----------------------------------------------------------------------------
export interface AffiliateForSelect {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
}

/**
 * Lista enxuta de afiliados (sem totais/status) para uso em selects de
 * filtros administrativos. Sem paginação — limita a 500 (mesmo limite
 * usado anteriormente). RLS no banco filtra por tenant.
 */
export function useAffiliatesForSelect() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useQuery<AffiliateForSelect[]>({
    queryKey: ['affiliates-for-select', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('affiliates')
        .select('id, full_name, email, avatar_url')
        .eq('tenant_id', tenantId!)
        .order('full_name', { ascending: true })
        .limit(500);

      if (error) {
        log.error('Erro ao listar afiliados para seletor', error);
        throw error;
      }
      return (data ?? []) as AffiliateForSelect[];
    },
  });
}

// -----------------------------------------------------------------------------
// Detalhes do afiliado + links + memberships (RPC única)
// -----------------------------------------------------------------------------
interface ReferralAffiliateDetailsPayload {
  affiliate: AffiliateRow;
  links: ReferralAffiliateLinkRow[];
  /** v2.0.0 — adesões da pessoa (modelo N:N). */
  memberships: AffiliateMembership[];
}

const detailsQueryKey = (tenantId: string | null, id: string | null) =>
  ['referral-affiliate-details', tenantId, id] as const;

/**
 * Hook compartilhado entre `useReferralAffiliate` e `useReferralAffiliateLinks`
 * (e quem mais quiser usar). React Query deduplica via queryKey — uma só
 * chamada de RPC por abertura do drawer.
 */
function useReferralAffiliateDetailsQuery(id: string | null) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useQuery<ReferralAffiliateDetailsPayload | null>({
    queryKey: detailsQueryKey(tenantId, id),
    enabled: !!tenantId && !!id,
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'get_referral_affiliate_details',
        {
          p_affiliate_id: id!,
          p_tenant_hint: tenantId!,
        },
      );
      if (error) {
        log.error('Erro ao carregar detalhes do afiliado (RPC)', error);
        throw error;
      }
      return (data as unknown as ReferralAffiliateDetailsPayload) ?? null;
    },
  });
}

/** Busca um afiliado específico por id (drawer de detalhes). */
export function useReferralAffiliate(id: string | null) {
  const query = useReferralAffiliateDetailsQuery(id);
  return {
    ...query,
    data: (query.data?.affiliate ?? null) as AffiliateRow | null,
  };
}

/** Lista de adesões da pessoa (memberships). */
export function useReferralAffiliateMemberships(id: string | null) {
  const query = useReferralAffiliateDetailsQuery(id);
  return {
    ...query,
    data: (query.data?.memberships ?? []) as AffiliateMembership[],
  };
}

// -----------------------------------------------------------------------------
// Mutations administrativas remanescentes (apenas notas internas)
// -----------------------------------------------------------------------------
// A moderação de status é feita por adesão (membership), via
// `useAffiliateMembershipActions`. Aqui mantemos apenas o que ainda existe
// no nível "pessoa": notas internas (`affiliates.notes`).
// -----------------------------------------------------------------------------
export function useReferralAffiliateMutations() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['referral-affiliates-paginated'] });
    qc.invalidateQueries({ queryKey: ['referral-affiliate-details'] });
  };

  const updateNotesMutation = useMutation({
    mutationFn: async (params: { id: string; notes: string | null }) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { error } = await supabase
        .from('affiliates')
        .update({ notes: params.notes })
        .eq('id', params.id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { updateNotesMutation };
}

// -----------------------------------------------------------------------------
// Cadastro administrativo (interno) — modelo N:N
// -----------------------------------------------------------------------------
export interface CreateAffiliateAdminInput {
  programId: string;
  fullName: string;
  email: string;
  phone?: string | null;
  documentNumber?: string | null;
  documentType?: 'cpf' | 'cnpj' | null;
  notes?: string | null;
  /** Quando true, aprova a adesão recém-criada se RPC retornar pending. */
  forceApprove?: boolean;
}

export function useCreateAffiliateAdmin() {
  const qc = useQueryClient();
  const { currentTenant, user } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async (input: CreateAffiliateAdminInput) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');

      // 1) Cria pessoa + adesão via RPC (resolve tenant via auth.uid()).
      const { data, error } = await supabase.rpc('create_affiliate', {
        p_program_id: input.programId,
        p_full_name: input.fullName,
        p_email: input.email,
        p_phone: input.phone ?? null,
        p_document_number: input.documentNumber ?? null,
        p_document_type: input.documentType ?? null,
        p_user_id: null,
        p_terms_accepted: true,
        p_metadata: {
          source: 'admin_manual',
          created_by_admin: user?.id ?? null,
        },
      });

      if (error) {
        log.error('Erro ao criar afiliado (admin)', error);
        throw error;
      }

      const result = Array.isArray(data) ? data[0] : data;
      if (!result?.affiliate_id) {
        throw new Error('Resposta inesperada da RPC create_affiliate');
      }

      const affiliateId = result.affiliate_id as string;
      const membershipId = result.membership_id as string;
      let finalStatus = result.status as AffiliateStatus;

      // 2) Notas internas (se informadas)
      if (input.notes && input.notes.trim()) {
        const { error: notesErr } = await supabase
          .from('affiliates')
          .update({ notes: input.notes.trim() })
          .eq('id', affiliateId)
          .eq('tenant_id', tenantId);
        if (notesErr) log.warn('Não foi possível salvar notas iniciais', notesErr);
      }

      // 3) Aprovação imediata da adesão (se solicitada e ainda pending).
      //    Modelo N:N: aprovação é por adesão.
      if (input.forceApprove && finalStatus === 'pending') {
        const { error: msErr } = await supabase
          .from('affiliate_program_memberships')
          .update({
            status: 'active',
            approved_at: new Date().toISOString(),
            approved_by: user?.id ?? null,
            rejection_reason: null,
          })
          .eq('id', membershipId)
          .eq('tenant_id', tenantId);
        if (msErr) log.warn('Não foi possível aprovar a adesão recém-criada', msErr);
        else finalStatus = 'active';
      }

      return { affiliateId, membershipId, status: finalStatus };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['referral-affiliates-paginated'] });
      qc.invalidateQueries({ queryKey: ['referral-dashboard-kpis'] });
      qc.invalidateQueries({ queryKey: ['affiliate-my-campaigns'] });
      qc.invalidateQueries({ queryKey: ['referral-affiliate-links'] });
      qc.invalidateQueries({ queryKey: ['affiliates-for-select'] });
      // v1.1.0: atualiza o drawer de detalhes (aba "Programas") ao vincular
      qc.invalidateQueries({ queryKey: ['referral-affiliate'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Tipos de link (mantidos para compat com componentes que importavam daqui)
// -----------------------------------------------------------------------------
export interface ReferralAffiliateLinkRow {
  id: string;
  slug: string;
  short_code: string | null;
  destination_url: string;
  is_active: boolean;
  campaign_name: string | null;
}

export function useReferralAffiliateLinks(affiliateId: string | null) {
  const query = useReferralAffiliateDetailsQuery(affiliateId);
  return {
    ...query,
    data: (query.data?.links ?? []) as ReferralAffiliateLinkRow[],
  };
}
